// Mock data for School Structure Setup page

export interface CycleConfig {
  id: string;
  type: 'weekly' | 'bi-weekly';
  totalPeriods: number;
  weekLabels: string[];
  cycleStartDate: string;
}

export interface Period {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  duration: number;
  type: 'lesson' | 'form' | 'assembly' | 'break' | 'lunch';
  yearGroups: string[];
  isTeaching: boolean;
}

export interface DailyStructure {
  id: string;
  name: string;
  schoolStart: string;
  schoolEnd: string;
  periods: Period[];
}

export interface Room {
  id: string;
  name: string;
  roomNumber: string;
  building: string;
  floor: number;
  type: 'standard' | 'laboratory' | 'workshop' | 'gymnasium' | 'hall' | 'ict' | 'art' | 'music';
  capacity: number;
  seatingArrangement: string;
  equipment: string[];
  facilities: string[];
  location: string;
  isShared: boolean;
  fixedTeacher?: string;
  accessibility: string[];
}

export interface ClassGroup {
  id: string;
  name: string;
  yearGroup: string;
  capacity: number;
  currentSize: number;
  formTutor: string;
  room: string;
}

export interface FormGroup {
  id: string;
  name: string;
  yearGroup: string;
  students: string[];
  formTutor: string;
  formRoom: string;
  formTime: string;
}

export interface YearGroup {
  id: string;
  name: string;
  yearNumber: number;
  totalStudents: number;
  classes: ClassGroup[];
  formGroups: FormGroup[];
}

export interface CycleVariation {
  id: string;
  subjectName: string;
  weekA: { periods: number; days?: string[] };
  weekB: { periods: number; days?: string[] };
}

export const mockData = {
  cycleConfig: {
    id: 'CC001',
    type: 'bi-weekly' as const,
    totalPeriods: 50,
    weekLabels: ['Week A', 'Week B'],
    cycleStartDate: '2024-09-02',
  } as CycleConfig,

  dailyStructure: {
    id: 'DS001',
    name: 'Standard School Day',
    schoolStart: '08:30',
    schoolEnd: '15:30',
    periods: [
      {
        id: 'P001',
        name: 'Registration',
        startTime: '08:30',
        endTime: '08:45',
        duration: 15,
        type: 'form' as const,
        yearGroups: ['Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11'],
        isTeaching: false,
      },
      {
        id: 'P002',
        name: 'Period 1',
        startTime: '08:45',
        endTime: '09:45',
        duration: 60,
        type: 'lesson' as const,
        yearGroups: ['Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11'],
        isTeaching: true,
      },
      {
        id: 'P003',
        name: 'Period 2',
        startTime: '09:45',
        endTime: '10:45',
        duration: 60,
        type: 'lesson' as const,
        yearGroups: ['Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11'],
        isTeaching: true,
      },
      {
        id: 'P004',
        name: 'Break',
        startTime: '10:45',
        endTime: '11:00',
        duration: 15,
        type: 'break' as const,
        yearGroups: ['Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11'],
        isTeaching: false,
      },
      {
        id: 'P005',
        name: 'Period 3',
        startTime: '11:00',
        endTime: '12:00',
        duration: 60,
        type: 'lesson' as const,
        yearGroups: ['Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11'],
        isTeaching: true,
      },
      {
        id: 'P006',
        name: 'Period 4',
        startTime: '12:00',
        endTime: '13:00',
        duration: 60,
        type: 'lesson' as const,
        yearGroups: ['Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11'],
        isTeaching: true,
      },
      {
        id: 'P007',
        name: 'Lunch',
        startTime: '13:00',
        endTime: '13:45',
        duration: 45,
        type: 'lunch' as const,
        yearGroups: ['Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11'],
        isTeaching: false,
      },
      {
        id: 'P008',
        name: 'Period 5',
        startTime: '13:45',
        endTime: '14:45',
        duration: 60,
        type: 'lesson' as const,
        yearGroups: ['Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11'],
        isTeaching: true,
      },
      {
        id: 'P009',
        name: 'Period 6',
        startTime: '14:45',
        endTime: '15:30',
        duration: 45,
        type: 'lesson' as const,
        yearGroups: ['Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11'],
        isTeaching: true,
      },
    ],
  } as DailyStructure,

  rooms: [
    {
      id: 'R001',
      name: 'Mathematics Room 1',
      roomNumber: 'M01',
      building: 'Main Building',
      floor: 1,
      type: 'standard' as const,
      capacity: 30,
      seatingArrangement: 'Rows',
      equipment: ['Interactive Whiteboard', 'Projector', 'Computer', 'Calculator Set'],
      facilities: ['Air Conditioning', 'WiFi', 'Power Outlets'],
      location: 'Block A, Floor 1',
      isShared: false,
      accessibility: ['Wheelchair Access'],
    },
    {
      id: 'R002',
      name: 'Science Laboratory 1',
      roomNumber: 'SL01',
      building: 'Science Block',
      floor: 2,
      type: 'laboratory' as const,
      capacity: 24,
      seatingArrangement: 'Lab Benches',
      equipment: ['Fume Cupboard', 'Gas Taps', 'Laboratory Benches', 'Safety Shower', 'Microscopes'],
      facilities: ['Emergency Eyewash', 'Fire Blanket', 'First Aid Kit', 'Chemical Storage'],
      location: 'Block B, Floor 2',
      isShared: true,
      accessibility: ['Emergency Exit', 'Safety Equipment'],
    },
    {
      id: 'R003',
      name: 'Computer Suite 1',
      roomNumber: 'ICT01',
      building: 'Technology Block',
      floor: 1,
      type: 'ict' as const,
      capacity: 28,
      seatingArrangement: 'U-Shape',
      equipment: ['30 Desktop Computers', 'Interactive Whiteboard', 'Network Printer', 'Server Access'],
      facilities: ['Air Conditioning', 'WiFi', 'Network Access', 'UPS Power'],
      location: 'Block C, Floor 1',
      isShared: true,
      accessibility: ['Wheelchair Access', 'Height Adjustable Desks'],
    },
    {
      id: 'R004',
      name: 'Sports Hall',
      roomNumber: 'SH01',
      building: 'Sports Complex',
      floor: 0,
      type: 'gymnasium' as const,
      capacity: 60,
      seatingArrangement: 'Open Space',
      equipment: ['Basketball Hoops', 'Volleyball Net', 'Gymnastics Equipment', 'Sound System'],
      facilities: ['Changing Rooms', 'Storage Room', 'First Aid Station', 'Spectator Seating'],
      location: 'Sports Block',
      isShared: true,
      accessibility: ['Wheelchair Access', 'Accessible Changing Rooms'],
    },
    {
      id: 'R005',
      name: 'Music Room 1',
      roomNumber: 'MU01',
      building: 'Arts Block',
      floor: 1,
      type: 'music' as const,
      capacity: 25,
      seatingArrangement: 'Flexible',
      equipment: ['Piano', 'Drums', 'Keyboards', 'Sound System', 'Recording Equipment'],
      facilities: ['Sound Proofing', 'Storage for Instruments', 'Practice Rooms'],
      location: 'Block D, Floor 1',
      isShared: false,
      accessibility: [],
    },
    {
      id: 'R006',
      name: 'Art Studio',
      roomNumber: 'AR01',
      building: 'Arts Block',
      floor: 2,
      type: 'art' as const,
      capacity: 20,
      seatingArrangement: 'Tables',
      equipment: ['Art Tables', 'Easels', 'Kiln', 'Storage Cabinets', 'Pottery Wheels'],
      facilities: ['Natural Light', 'Ventilation', 'Water Access', 'Clay Storage'],
      location: 'Block D, Floor 2',
      isShared: false,
      accessibility: ['Wheelchair Access'],
    },
  ] as Room[],

  yearGroups: [
    {
      id: 'YG7',
      name: 'Year 7',
      yearNumber: 7,
      totalStudents: 180,
      classes: [
        {
          id: 'C7A',
          name: '7A',
          yearGroup: 'Year 7',
          capacity: 30,
          currentSize: 28,
          formTutor: 'Ms. Johnson',
          room: 'R101',
        },
        {
          id: 'C7B',
          name: '7B',
          yearGroup: 'Year 7',
          capacity: 30,
          currentSize: 30,
          formTutor: 'Mr. Williams',
          room: 'R102',
        },
        {
          id: 'C7C',
          name: '7C',
          yearGroup: 'Year 7',
          capacity: 30,
          currentSize: 29,
          formTutor: 'Mrs. Brown',
          room: 'R103',
        },
        {
          id: 'C7D',
          name: '7D',
          yearGroup: 'Year 7',
          capacity: 30,
          currentSize: 31,
          formTutor: 'Mr. Davis',
          room: 'R104',
        },
        {
          id: 'C7E',
          name: '7E',
          yearGroup: 'Year 7',
          capacity: 30,
          currentSize: 32,
          formTutor: 'Ms. Wilson',
          room: 'R105',
        },
        {
          id: 'C7F',
          name: '7F',
          yearGroup: 'Year 7',
          capacity: 30,
          currentSize: 30,
          formTutor: 'Mr. Taylor',
          room: 'R106',
        },
      ],
      formGroups: [
        {
          id: 'FG7A',
          name: '7A',
          yearGroup: 'Year 7',
          students: ['S001', 'S002', 'S003'],
          formTutor: 'Ms. Johnson',
          formRoom: 'R101',
          formTime: '08:30',
        },
        {
          id: 'FG7B',
          name: '7B',
          yearGroup: 'Year 7',
          students: ['S004', 'S005', 'S006'],
          formTutor: 'Mr. Williams',
          formRoom: 'R102',
          formTime: '08:30',
        },
        {
          id: 'FG7C',
          name: '7C',
          yearGroup: 'Year 7',
          students: ['S007', 'S008', 'S009'],
          formTutor: 'Mrs. Brown',
          formRoom: 'R103',
          formTime: '08:30',
        },
        {
          id: 'FG7D',
          name: '7D',
          yearGroup: 'Year 7',
          students: ['S010', 'S011', 'S012'],
          formTutor: 'Mr. Davis',
          formRoom: 'R104',
          formTime: '08:30',
        },
        {
          id: 'FG7E',
          name: '7E',
          yearGroup: 'Year 7',
          students: ['S013', 'S014', 'S015'],
          formTutor: 'Ms. Wilson',
          formRoom: 'R105',
          formTime: '08:30',
        },
        {
          id: 'FG7F',
          name: '7F',
          yearGroup: 'Year 7',
          students: ['S016', 'S017', 'S018'],
          formTutor: 'Mr. Taylor',
          formRoom: 'R106',
          formTime: '08:30',
        },
      ],
    },
    {
      id: 'YG8',
      name: 'Year 8',
      yearNumber: 8,
      totalStudents: 175,
      classes: [
        {
          id: 'C8A',
          name: '8A',
          yearGroup: 'Year 8',
          capacity: 30,
          currentSize: 29,
          formTutor: 'Ms. Anderson',
          room: 'R201',
        },
        {
          id: 'C8B',
          name: '8B',
          yearGroup: 'Year 8',
          capacity: 30,
          currentSize: 30,
          formTutor: 'Mr. Thompson',
          room: 'R202',
        },
        {
          id: 'C8C',
          name: '8C',
          yearGroup: 'Year 8',
          capacity: 30,
          currentSize: 28,
          formTutor: '',
          room: 'R203',
        },
        {
          id: 'C8D',
          name: '8D',
          yearGroup: 'Year 8',
          capacity: 30,
          currentSize: 31,
          formTutor: 'Mrs. Garcia',
          room: 'R204',
        },
        {
          id: 'C8E',
          name: '8E',
          yearGroup: 'Year 8',
          capacity: 30,
          currentSize: 29,
          formTutor: 'Mr. Martinez',
          room: 'R205',
        },
        {
          id: 'C8F',
          name: '8F',
          yearGroup: 'Year 8',
          capacity: 30,
          currentSize: 28,
          formTutor: 'Ms. Lee',
          room: 'R206',
        },
      ],
      formGroups: [
        {
          id: 'FG8A',
          name: '8A',
          yearGroup: 'Year 8',
          students: ['S019', 'S020', 'S021'],
          formTutor: 'Ms. Anderson',
          formRoom: 'R201',
          formTime: '08:30',
        },
        {
          id: 'FG8B',
          name: '8B',
          yearGroup: 'Year 8',
          students: ['S022', 'S023', 'S024'],
          formTutor: 'Mr. Thompson',
          formRoom: 'R202',
          formTime: '08:30',
        },
        {
          id: 'FG8C',
          name: '8C',
          yearGroup: 'Year 8',
          students: ['S025', 'S026', 'S027'],
          formTutor: '',
          formRoom: 'R203',
          formTime: '08:30',
        },
        {
          id: 'FG8D',
          name: '8D',
          yearGroup: 'Year 8',
          students: ['S028', 'S029', 'S030'],
          formTutor: 'Mrs. Garcia',
          formRoom: 'R204',
          formTime: '08:30',
        },
        {
          id: 'FG8E',
          name: '8E',
          yearGroup: 'Year 8',
          students: ['S031', 'S032', 'S033'],
          formTutor: 'Mr. Martinez',
          formRoom: 'R205',
          formTime: '08:30',
        },
        {
          id: 'FG8F',
          name: '8F',
          yearGroup: 'Year 8',
          students: ['S034', 'S035', 'S036'],
          formTutor: 'Ms. Lee',
          formRoom: 'R206',
          formTime: '08:30',
        },
      ],
    },
  ] as YearGroup[],

  cycleVariations: [
    {
      id: 'CV001',
      subjectName: 'Drama',
      weekA: { periods: 2, days: ['Tuesday', 'Thursday'] },
      weekB: { periods: 0, days: [] },
    },
    {
      id: 'CV002',
      subjectName: 'Music Ensemble',
      weekA: { periods: 1, days: ['Friday'] },
      weekB: { periods: 2, days: ['Monday', 'Friday'] },
    },
    {
      id: 'CV003',
      subjectName: 'Extended Science',
      weekA: { periods: 3, days: ['Monday', 'Wednesday', 'Friday'] },
      weekB: { periods: 4, days: ['Monday', 'Tuesday', 'Wednesday', 'Friday'] },
    },
  ] as CycleVariation[],
};
